/**
 * DynamicJobForm Component
 *
 * Renders a form with custom fields based on job type definition.
 * Handles field state and validation errors.
 *
 * DDD Compliance:
 * - Purely presentational
 * - Uses real API via React Query hook
 * - Parent manages form state
 * - Displays validation errors passed from parent
 */

import { CustomFieldInput } from './CustomFieldInput';
import { useJobType } from '@/application/service-provider/hooks/useJobTypes';

export interface DynamicJobFormProps {
  jobTypeId?: string;
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

export function DynamicJobForm({
  jobTypeId,
  values,
  onChange,
  errors,
  disabled,
}: DynamicJobFormProps) {
  // Fetch job type using React Query
  const {
    data: jobType,
    isLoading,
    isError,
  } = useJobType(jobTypeId);

  // No job type selected
  if (!jobTypeId) {
    return (
      <div className="text-center py-8 text-neutral-500 text-sm">
        <p>Select a job type to configure custom fields</p>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i}>
            <div className="h-4 bg-neutral-200 rounded w-1/4 mb-2"></div>
            <div className="h-10 bg-neutral-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  // Error loading job type
  if (isError) {
    return (
      <div className="text-center py-8 text-red-500 text-sm">
        <p>Error loading job type. Please try again.</p>
      </div>
    );
  }

  // Job type not found
  if (!jobType) {
    return (
      <div className="text-center py-8 text-red-500 text-sm">
        <p>Job type not found</p>
      </div>
    );
  }

  // No fields defined
  if (!jobType.fieldDefinitions || jobType.fieldDefinitions.length === 0) {
    return (
      <div className="text-center py-8 text-neutral-500 text-sm">
        <p>This job type has no custom fields configured</p>
      </div>
    );
  }

  // Sort fields by display order
  const sortedFields = [...jobType.fieldDefinitions].sort(
    (a, b) => a.displayOrder - b.displayOrder
  );

  const handleFieldChange = (fieldName: string, value: unknown) => {
    onChange({
      ...values,
      [fieldName]: value,
    });
  };

  return (
    <div className="space-y-6">
      <div className="border-t pt-4">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">
          {jobType.name} - Custom Fields
        </h3>
        <div className="space-y-4">
          {sortedFields.map((field) => (
            <CustomFieldInput
              key={field.name}
              definition={field}
              value={values[field.name]}
              onChange={(value) => handleFieldChange(field.name, value)}
              error={errors?.[field.name]}
              disabled={disabled}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
